import { Focusable } from '@ariakit/react'
import { format } from 'date-fns'
import { FC, useCallback, useMemo, useState } from 'react'

import {
  ExpandableText,
  ExpansionPanel,
  MediaThumbnail,
  StarRating,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { NextDialogProvider } from '../../../../../app/_components/dialogs/NextDialog.client'
import { NotFoundPage } from '../../../../../app/_not-found.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { ModalCloseButton } from '../../../../../brunswick/components/ModalCloseButton'
import * as Review from '../../../../../components/reviews/Review.styles'
import { ProductReviewsContainer } from '../../../../../containers/ProductReviewsContainer'
import { trackEvent } from '../../../../../lib/analytics'
import { fieldsByFieldId } from '../../../../../lib/reviews/constants'
import {
  getFieldValue,
  getTranslatedValueFromReviewsFilter,
  isReviewMediaImage,
  isReviewsTranslatedFilterWithValuesKey,
} from '../../../../../lib/reviews/utils'
import { ProductReview } from '../../../../../types/graphql'
import { useModalState } from '../../../../modals/useModalState'
import * as ReviewDialog from '../../../../reviews/ReviewDetailsDialog/ReviewDetailsDialog.styles'
import {
  ReviewMediaTile,
  ReviewPhotosSection,
} from '../../../../reviews/ReviewPhotosSection/ReviewPhotosSection'
import {
  ReviewMedia,
  ReviewsSwiperMediaSlider,
} from '../../../../reviews/ReviewsSwiperMediaSlider/ReviewsSwiperMediaSlider'
import { Filters } from '../Filters'
import { ProductFilteredReviewsEmptyState } from '../ProductFilteredReviewsEmptyState'
import { ProductReviewsEmptyState } from '../ProductReviewsEmptyState'
import { ProductReviewsHeader } from '../ProductReviewsHeader'
import { ProductReviewsLoadingIndicator } from '../ProductReviewsLoadingIndicator'
import { ProductReviewsPagination } from '../ProductReviewsPagination'
import { ProductReviewsTableScrollOnPageChange } from '../ProductReviewsTableScrollOnPageChange'
import { ReviewBaseline } from '../ReviewBaseline'
import { ReviewForm } from '../ReviewForm'
import { ReviewHeader } from '../ReviewHeader'

import {
  BaselineWrapper,
  Heart,
  Main,
  NewFormWrapper,
  ProductReviewCardWrapper,
  ProductReviewPhotosWrapper,
  ProductReviewsList,
  ProductReviewsWrapper,
  ReviewsLayout,
  ReviewsSection,
  ThankYouHeadline,
  ThankYouSection,
} from './styles'

export const Template: FC<{
  didQueryFail: boolean
  dismissThankYou(): void
  handle: string
  hideAndDisableReviewForm(): void
  reviewSubmitted: boolean
  showForm: boolean
  thankYouDismissed: boolean
  thankYouHeadline: string
  toggleForm(): void
}> = ({
  didQueryFail,
  dismissThankYou,
  handle,
  hideAndDisableReviewForm,
  reviewSubmitted,
  showForm,
  thankYouDismissed,
  thankYouHeadline,
  toggleForm,
}) => {
  const {
    detailsSection: { reviewPhotosTitle },
    magnolia: {
      reviewsPage: {
        readMore,
        readLess,
        filters: { filter: filterFixtures },
      },
      navigation,
    },
  } = useTranslation()
  const {
    isOpen: isReviewDetailsDialogOpen,
    openModal: openReviewDetailsDialog,
    closeModal: closeReviewDetailsDialog,
  } = useModalState()
  const isSmallViewport = useIsViewport(ViewportSizes['small'])
  const [selectedReview, setSelectedReview] = useState<ProductReview | undefined>()
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | undefined>()
  const { results } = ProductReviewsContainer.useContainer()
  const reviews = results?.reviews

  const onThumbnailClick = useCallback(
    (reviewIndex: number, mediaIndex?: number) => {
      setSelectedReview(reviews![reviewIndex])
      setSelectedMediaIndex(mediaIndex)
      openReviewDetailsDialog()
    },
    [openReviewDetailsDialog, reviews]
  )

  const aggregateReviewMedia = useMemo(() => {
    const output: ReviewMediaTile[] = []
    reviews?.forEach(({ imageUrls }, reviewIndex) => {
      if (imageUrls) {
        output.push(
          ...imageUrls.map<ReviewMediaTile>((e, mediaIndex) => ({
            src: e,
            type: isReviewMediaImage(e) ? 'image' : 'video',
            reviewIndex,
            mediaIndex,
          }))
        )
      }
    })
    return output
  }, [reviews])

  const swiperAssets: ReviewMedia[] | undefined = useMemo(() => {
    if (selectedReview && selectedReview.imageUrls) {
      return selectedReview.imageUrls.map(url => ({
        src: url,
        type: isReviewMediaImage(url) ? 'image' : 'video',
      }))
    }
    return undefined
  }, [selectedReview])

  if (didQueryFail) return <NotFoundPage />

  return (
    <NextDialogProvider open={isReviewDetailsDialogOpen}>
      <Main>
        <ReviewHeader reviewSubmitted={reviewSubmitted} onWriteReview={toggleForm} />
        <BaselineWrapper>
          <ReviewBaseline />
        </BaselineWrapper>
        <NewFormWrapper>
          <ExpansionPanel.Container isExpanded={!reviewSubmitted && showForm}>
            <ExpansionPanel.Details>
              <ReviewForm handle={handle} hideAndDisableReviewForm={hideAndDisableReviewForm} />
            </ExpansionPanel.Details>
          </ExpansionPanel.Container>
        </NewFormWrapper>
        {reviewSubmitted && !thankYouDismissed && (
          <ThankYouSection>
            <ModalCloseButton onClick={dismissThankYou} />
            <Heart />
            <ThankYouHeadline>{thankYouHeadline}</ThankYouHeadline>
          </ThankYouSection>
        )}
        <ProductReviewsTableScrollOnPageChange />
        <ReviewsSection>
          <ReviewsLayout>
            <Filters />

            <ProductReviewsWrapper>
              <ProductReviewsHeader />

              {aggregateReviewMedia.length > 0 ? (
                <ProductReviewPhotosWrapper>
                  <ReviewPhotosSection
                    tiles={aggregateReviewMedia}
                    tileWidthMobile={104}
                    tileWidthDesktop={150.5}
                    tileGapMobile={8}
                    tileGapDesktop={10.5}
                    titleText={reviewPhotosTitle}
                    renderTile={(tile: ReviewMediaTile, index: number) => (
                      <ReviewDialog.ThumbnailTarget
                        onClick={() => onThumbnailClick(tile.reviewIndex, tile.mediaIndex)}
                        render={
                          <Focusable>
                            <MediaThumbnail
                              src={tile.src}
                              isVideo={tile.type === 'video'}
                              loading='lazy'
                              alt={`Review Media Thumbnail ${index + 1}`}
                              aspectRatios={{ sm: 0.75, md: 0.75 }}
                              widths={{ unit: 'px', sm: 96, md: 140 }}
                            />
                          </Focusable>
                        }
                      />
                    )}
                  />
                </ProductReviewPhotosWrapper>
              ) : null}
              <ProductReviewsList>
                {reviews && reviews.length > 0
                  ? reviews.map((review, reviewIndex) => (
                      <ProductReviewCardWrapper key={`${review.title}-${review.createdAt}`}>
                        <Review.Card isPDP={false}>
                          <Review.ReviewContent>
                            <Review.Header>
                              <StarRating rating={review.score} starSize='16px' />
                              {review.createdAt ? (
                                <Review.CreatedAtDate asTag='span'>
                                  {format(review.createdAt, 'MMMM d, yyyy')}
                                </Review.CreatedAtDate>
                              ) : null}
                            </Review.Header>
                            <Review.Title asTag='h2'>{review.title}</Review.Title>
                            <Review.BodyText>
                              <ExpandableText
                                content={review.content}
                                truncateLength={260}
                                readMoreText={readMore}
                                readLessText={readLess}
                              />
                            </Review.BodyText>
                            {review.imageUrls?.length ? (
                              <Review.ThumbnailsSection>
                                {review.imageUrls.map((url, urlIndex) => {
                                  if (isSmallViewport && urlIndex > 1) return null
                                  const isImage =
                                    /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff?|ico|apng|avif|heif|heic)(\?[^ ]*)?$/.test(
                                      url
                                    )
                                  return (
                                    <Review.ThumbnailWrapper key={url}>
                                      <ReviewDialog.ThumbnailTarget
                                        onClick={() => onThumbnailClick(reviewIndex, urlIndex)}
                                        render={
                                          <Focusable>
                                            <MediaThumbnail
                                              src={url}
                                              alt={`Review Media Thumbnail ${urlIndex + 1}`}
                                              isVideo={!isImage}
                                              aspectRatios={{ sm: 0.75, md: 0.75 }}
                                              loading='default'
                                              widths={{ unit: 'px', sm: 96, md: 66 }}
                                              {...trackEvent({
                                                category: 'reviews page',
                                                action: 'click tile',
                                                value: 'image',
                                              })}
                                            />
                                          </Focusable>
                                        }
                                      />
                                    </Review.ThumbnailWrapper>
                                  )
                                })}
                                {review.imageUrls.length - 2 > 0 ? (
                                  <Review.ThumbnailsOverflow>
                                    {`+ ${review.imageUrls.length - 2}`}
                                  </Review.ThumbnailsOverflow>
                                ) : null}
                              </Review.ThumbnailsSection>
                            ) : null}
                          </Review.ReviewContent>
                          {review.customFields && review.customFields.length > 0 ? (
                            <Review.PurchaseDetailsWrapper hasContent>
                              <Review.PurchaseDetails>
                                <Review.ReviewerName>{review.reviewer}</Review.ReviewerName>
                                <Review.DetailsList>
                                  {review.customFields
                                    .map(({ key, value }) => {
                                      const field = fieldsByFieldId[key!]
                                      return {
                                        id: field?.fieldId,
                                        name: field?.name,
                                        value: field ? getFieldValue(field, value!) : undefined,
                                      }
                                    })
                                    .map(({ id, name, value }) => {
                                      if (!id || !name || !value) return null

                                      const isNameValidKey =
                                        isReviewsTranslatedFilterWithValuesKey(name)

                                      const localizedName = isNameValidKey
                                        ? filterFixtures[name].title
                                        : undefined

                                      const localizedValue = isNameValidKey
                                        ? getTranslatedValueFromReviewsFilter({
                                            valueKey: value,
                                            filter: filterFixtures[name],
                                          })
                                        : undefined

                                      return (
                                        <Review.DetailsItem key={id}>
                                          <b>{localizedName ?? name}:</b> {localizedValue ?? value}
                                        </Review.DetailsItem>
                                      )
                                    })}
                                </Review.DetailsList>
                              </Review.PurchaseDetails>
                            </Review.PurchaseDetailsWrapper>
                          ) : null}
                        </Review.Card>
                      </ProductReviewCardWrapper>
                    ))
                  : null}
              </ProductReviewsList>

              <ProductFilteredReviewsEmptyState />
              <ProductReviewsLoadingIndicator />
            </ProductReviewsWrapper>
          </ReviewsLayout>

          <ProductReviewsPagination />

          <ReviewDialog.Dialog onClose={closeReviewDetailsDialog}>
            <ReviewDialog.DialogContent>
              <ReviewDialog.CloseButtonWrapper displayOnMobile={true}>
                <ReviewDialog.CloseButton
                  onClick={closeReviewDetailsDialog}
                  aria-label={navigation.closeButtonLabel}
                  {...trackEvent({ category: 'reviews modal', action: 'close' })}
                />
              </ReviewDialog.CloseButtonWrapper>
              <ReviewDialog.SwiperWrapper>
                {swiperAssets && swiperAssets.length > 0 ? (
                  <ReviewsSwiperMediaSlider
                    sliderAssets={swiperAssets}
                    initialSlideIndex={selectedMediaIndex}
                  />
                ) : null}
              </ReviewDialog.SwiperWrapper>
              <ReviewDialog.ReviewWrapper>
                <ReviewDialog.CloseButtonWrapper displayOnMobile={false}>
                  <ReviewDialog.CloseButton
                    onClick={closeReviewDetailsDialog}
                    aria-label={navigation.closeButtonLabel}
                    {...trackEvent({ category: 'reviews modal', action: 'close' })}
                  />
                </ReviewDialog.CloseButtonWrapper>
                {selectedReview ? (
                  <ReviewDialog.ReviewContent>
                    <ReviewDialog.Header>
                      <StarRating rating={selectedReview.score} starSize='16px' />
                      {selectedReview.createdAt ? (
                        <ReviewDialog.CreatedAtDate asTag='span'>
                          {format(selectedReview.createdAt, 'MMMM d, yyyy')}
                        </ReviewDialog.CreatedAtDate>
                      ) : null}
                    </ReviewDialog.Header>
                    <ReviewDialog.Title asTag='h2'>{selectedReview.title}</ReviewDialog.Title>
                    <ReviewDialog.BodyText>
                      <ExpandableText
                        content={selectedReview.content}
                        truncateLength={260}
                        readMoreText={readMore}
                        readLessText={readLess}
                      />
                    </ReviewDialog.BodyText>
                    {selectedReview.customFields && selectedReview.customFields.length > 0 ? (
                      <ReviewDialog.PurchaseDetailsWrapper hasContent>
                        <ReviewDialog.PurchaseDetails>
                          <ReviewDialog.ReviewerName>
                            {selectedReview.reviewer}
                          </ReviewDialog.ReviewerName>
                          <ReviewDialog.DetailsList>
                            {selectedReview.customFields
                              .map(({ key, value }) => {
                                const field = fieldsByFieldId[key!]
                                return {
                                  field,
                                  value: field ? getFieldValue(field, value!) : undefined,
                                }
                              })
                              .filter(({ field, value }) => field && value)
                              .map(({ field, value }) => (
                                <ReviewDialog.DetailsItem key={field!.fieldId}>
                                  <b>{field!.name}:</b> {value}
                                </ReviewDialog.DetailsItem>
                              ))}
                          </ReviewDialog.DetailsList>
                        </ReviewDialog.PurchaseDetails>
                      </ReviewDialog.PurchaseDetailsWrapper>
                    ) : null}
                  </ReviewDialog.ReviewContent>
                ) : null}
              </ReviewDialog.ReviewWrapper>
            </ReviewDialog.DialogContent>
          </ReviewDialog.Dialog>

          {!showForm && (
            <ProductReviewsEmptyState
              reviewSubmitted={reviewSubmitted}
              onWriteReview={toggleForm}
            />
          )}
        </ReviewsSection>
      </Main>
    </NextDialogProvider>
  )
}
